import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import useBusinessType from '@/shared/hooks/useBusinessType'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import { pageConfigurationActions } from '@/store/PageConfiguration/PageConfigurationSagas'
import {
  selectPageConfigurationData,
  selectPageConfigurationIsLoading,
} from '@/store/PageConfiguration/PageConfigurationSelectors'
import useDashboardFiltersParams from './DashboardFilters/useDashboardFiltersParams'
import { selectFilters, selectPage, selectUrl } from './DashboardV2Selectors'
import { setFilters, setUrl, setPage, setCurrentPage, clearDashboardV2 } from './DashboardV2Slice'
import { dashboardV2Actions } from './DashboardV2Sagas'
import { useNavigate } from 'react-router'

const useDashboardV2 = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { loaderRoutesEnergy, loaderRoutes, loaderRoutesInsurance } = useSelector(selectAuthData) as any
  const { getApplicationUrl, type: applicationType } = useBusinessType({
    loaderRoutesEnergy,
    loaderRoutes,
    loaderRoutesInsurance,
    modulesStatuses: {},
    modulesStatusesEnergy: {},
    modulesStatusesInsurance: {},
  })
  const navigateToBusinessVertical = (event: React.SyntheticEvent, index: number) => {
    event.stopPropagation()
    if (index === 0) {
      navigate('/energie/contracts')
    } else if (index === 1) {
      navigate('/telecom/contracts')
    } else if (index === 2) {
      navigate('/assurance/contracts')
    }
  }

  const initUrl = getApplicationUrl('contacts-dashboard') ?? ''

  const isLoading = useSelector(selectPageConfigurationIsLoading)

  const { tableUrl, prefilters, users, providers, endpoint } = useSelector(selectPageConfigurationData) as any
  const filters = useSelector(selectFilters)
  const page = useSelector(selectPage)
  const url = useSelector(selectUrl)

  const manageURL = (pageToUse: number | string) => {
    if (!tableUrl) return ''

    const dashboardFiltersParams = useDashboardFiltersParams(filters, applicationType)
    const params = dashboardFiltersParams.create()

    if (pageToUse) {
      params.push(`cursor=${pageToUse}`)
    }

    if (tableUrl.includes('?')) {
      return tableUrl + '&' + params.join('&')
    }

    return tableUrl + '?' + params.join('&')
  }

  const getContracts = () => {
    dispatch({ type: dashboardV2Actions.LOAD_CONTRACTS, url })
  }

  const updateUrlHandler = (pageToUse: number | string) => {
    const newUrl = manageURL(pageToUse)
    if (!filters || !pageToUse || !newUrl) return
    dispatch(setUrl(newUrl))
  }

  const initDashboardPage = () => {
    dispatch(setPage(1))
    dispatch(setUrl(''))
  }

  // Initialize PageConfiguration
  useEffect(() => {
    dispatch({ type: pageConfigurationActions.INITIALIZE, initUrl })
    window.addEventListener('beforeunload', initDashboardPage)
    return () => {
      window.removeEventListener('beforeunload', initDashboardPage)
      dispatch(clearDashboardV2())
    }
  }, [applicationType])

  // Update URL when page or tableUrl changes
  useEffect(() => {
    updateUrlHandler(page)
  }, [page, tableUrl])

  // Reset to page 1 when filters change
  useEffect(() => {
    dispatch(setCurrentPage(1))
    updateUrlHandler(1)
  }, [filters])

  // Apply prefilters from PageConfiguration
  useEffect(() => {
    if (!prefilters) return
    dispatch(setFilters(JSON.parse(prefilters)))
  }, [prefilters])

  // Load contracts when URL changes
  useEffect(() => {
    if (url) {
      getContracts()
    }
  }, [url])

  const formInitUrl = getApplicationUrl('telesales-form-new') ?? ''

  return {
    t,
    isLoading,
    getContracts,
    filters,
    usersList: users,
    providersList: providers,
    applicationType,
    endpoint,
    formInitUrl,
    navigateToBusinessVertical,
  }
}

export default useDashboardV2
